import React, { useEffect, useState } from 'react';
import { MEDALS } from './tournaments/format.js';

function formatProfit(profit) {
  const sign = profit >= 0 ? '+' : '−';
  return `${sign}Ṁ${Math.abs(profit).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function profitClass(profit) {
  if (profit > 0) return 'profit-positive';
  if (profit < 0) return 'profit-negative';
  return 'profit-neutral';
}

export default function Leaderboard({ tournamentId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [markets, setMarkets] = useState({});

  useEffect(() => {
    if (!tournamentId) return;

    setLoading(true);
    setError(null);

    fetch(`/api/tournaments/${tournamentId}/leaderboard`)
      .then(r => {
        if (!r.ok) throw new Error('Failed to fetch leaderboard');
        return r.json();
      })
      .then(data => {
        setLeaderboard(data);

        const marketIds = new Set();
        data.forEach(user => {
          user.marketProfits?.forEach(mp => marketIds.add(mp.marketId));
        });

        if (marketIds.size > 0) {
          Promise.all(
            Array.from(marketIds).map(marketId =>
              fetch(`/api/manifold/market/${marketId}`)
                .then(r => r.json())
                .then(market => ({ [marketId]: market }))
                .catch(() => ({ [marketId]: { question: marketId, error: true } }))
            )
          ).then(marketData => {
            setMarkets(Object.assign({}, ...marketData));
          });
        }
      })
      .catch(err => {
        console.error('Error fetching leaderboard:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [tournamentId]);

  const toggleExpanded = (userId) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  if (loading) {
    return <div className="center-message">Loading leaderboard...</div>;
  }

  if (error) {
    return <div className="center-message error-text">Failed to load leaderboard: {error}</div>;
  }

  if (leaderboard.length === 0) {
    return (
      <div className="center-message">
        No participants yet — trade in any tournament market to appear here.
      </div>
    );
  }

  return (
    <div className="leaderboard">
      {leaderboard.map((user) => {
        const isExpanded = expandedUsers.has(user.userId);
        const hasMarketData = user.marketProfits && user.marketProfits.length > 0;

        return (
          <div key={user.userId}>
            <div
              className={`leaderboard-row ${hasMarketData ? 'expandable' : ''} ${isExpanded ? 'expanded' : ''}`}
              onClick={() => hasMarketData && toggleExpanded(user.userId)}
            >
              <div className="leaderboard-user">
                <span className={`leaderboard-rank ${user.rank <= 3 ? 'top-rank' : ''}`}>
                  {MEDALS[user.rank] || `#${user.rank}`}
                </span>
                {user.avatarUrl && <img src={user.avatarUrl} alt="" className="avatar" />}
                <a
                  href={`/user.html?u=${encodeURIComponent(user.username || user.userId)}`}
                  className="leaderboard-name"
                  onClick={(e) => e.stopPropagation()}
                >
                  {user.userName}
                </a>
              </div>
              <div className="leaderboard-right">
                <span className={`leaderboard-profit ${profitClass(user.totalProfit)}`}>
                  {formatProfit(user.totalProfit)}
                </span>
                {hasMarketData && <span className={`expand-caret ${isExpanded ? 'open' : ''}`}>▾</span>}
              </div>
            </div>

            {isExpanded && hasMarketData && (
              <div className="leaderboard-breakdown">
                {[...user.marketProfits]
                  .sort((a, b) => b.profit - a.profit)
                  .map((mp, index) => {
                    const market = markets[mp.marketId];
                    return (
                      <div key={index} className="breakdown-row">
                        <span className={`breakdown-market ${market?.error ? 'muted' : ''}`}>
                          {market?.question || mp.marketId}
                          {market?.error && ' (failed to load)'}
                        </span>
                        <span className={`breakdown-profit ${profitClass(mp.profit)}`}>
                          {formatProfit(mp.profit)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
